import type { CellContext, RowData } from '@tanstack/react-table';
import { useCallback, useMemo, useSyncExternalStore } from 'react';

import type { EditableDraftRow, RenderFromDraft } from '../types';
import { useStagedValueContext } from './staged-value-context';

type RenderFromDraftCellProps<TData extends RowData, TValue> = {
  context: CellContext<TData, TValue>;
  renderFromDraft: RenderFromDraft<TData>;
};

export function RenderFromDraftCell<TData extends RowData, TValue = unknown>({
  context,
  renderFromDraft,
}: RenderFromDraftCellProps<TData, TValue>) {
  const stagedValueStore = useStagedValueContext();
  const rowId = context.row.id;

  const subscribe = useCallback(
    (listener: () => void) => stagedValueStore.subscribeStagedRow(rowId, listener),
    [rowId, stagedValueStore],
  );
  const getSnapshot = useCallback(
    () => stagedValueStore.getRowSnapshot(rowId),
    [rowId, stagedValueStore],
  );
  const stagedSnapshot = useSyncExternalStore(subscribe, getSnapshot);

  const draft = useMemo(
    () =>
      Object.freeze({
        ...(context.row.original as Record<string, unknown>),
        ...stagedSnapshot,
      }) as EditableDraftRow<TData>,
    [context.row.original, stagedSnapshot],
  );

  return renderFromDraft(draft, context.row);
}
